import type { AppData } from "./AppContainer";
import type { WorkspaceNode } from "./WorkPanel";
import type { ConversationMeta } from "./chat/types";

export type WorkspaceCollectionId = "conversations" | "projects" | "workstreams" | "apps";
export type WorkspaceViewId = "life" | "comms" | "files";

export type WorkspaceNodeDestination =
  | { kind: "view"; view: WorkspaceViewId }
  | { kind: "collection"; collectionId: WorkspaceCollectionId }
  | { kind: "app"; appId: string }
  | { kind: "conversation"; sessionId: string }
  | { kind: "detail"; nodeId: string }
  | null;

const COLLECTION_IDS = new Set<WorkspaceCollectionId>([
  "conversations",
  "projects",
  "workstreams",
  "apps",
]);

interface DigestSummary {
  workstreams: number;
  openIssues: number;
}

export function isWorkspaceCollectionRoot(node: WorkspaceNode): node is WorkspaceNode & {
  id: WorkspaceCollectionId;
} {
  return node.type === "category" && COLLECTION_IDS.has(node.id as WorkspaceCollectionId);
}

export function resolveWorkspaceNodeDestination(node: WorkspaceNode): WorkspaceNodeDestination {
  if (node.type === "life" || node.id === "life") {
    return { kind: "view", view: "life" };
  }

  if (isWorkspaceCollectionRoot(node)) {
    return { kind: "collection", collectionId: node.id };
  }

  if (node.type === "app") {
    if (node.id === "app-comms") {
      return { kind: "view", view: "comms" };
    }
    return { kind: "app", appId: node.id };
  }

  if (node.type === "file" || node.id === "files") {
    return { kind: "view", view: "files" };
  }

  if (node.id.startsWith("conv-") && node.id !== "conv-onboarding") {
    return { kind: "conversation", sessionId: node.id.replace("conv-", "") };
  }

  if (node.type === "project" || node.type === "workstream" || node.type === "issue") {
    return { kind: "detail", nodeId: node.id };
  }

  const linkedSessionId =
    typeof node.metadata?.sessionId === "string" ? node.metadata.sessionId : undefined;
  if (linkedSessionId) {
    return { kind: "conversation", sessionId: linkedSessionId };
  }

  return null;
}

export function opensWorkspaceNodeOnPrimaryClick(node: WorkspaceNode): boolean {
  return resolveWorkspaceNodeDestination(node) !== null;
}

export function buildWorkPanelDigestText(summary: DigestSummary): string {
  if (summary.workstreams === 0 && summary.openIssues === 0) {
    return "Start with a conversation, project, or workstream.";
  }
  return `${summary.workstreams} workstream${summary.workstreams !== 1 ? "s" : ""} · ${summary.openIssues} open issue${summary.openIssues !== 1 ? "s" : ""}`;
}

export function buildWorkspaceCollectionApp({
  collectionId,
  nodes,
  conversations = [],
}: {
  collectionId: WorkspaceCollectionId;
  nodes: WorkspaceNode[];
  conversations?: ConversationMeta[];
}): AppData {
  switch (collectionId) {
    case "conversations":
      return {
        id: "workspace-collection-conversations",
        title: "Conversations",
        designSystem: true,
        html: buildConversationCollectionHtml(conversations),
        sourceMode: "legacy_html",
      };
    case "projects":
      return {
        id: "workspace-collection-projects",
        title: "Projects",
        designSystem: true,
        html: buildProjectsCollectionHtml(nodes),
        sourceMode: "legacy_html",
      };
    case "workstreams":
      return {
        id: "workspace-collection-workstreams",
        title: "Workstreams",
        designSystem: true,
        html: buildWorkstreamsCollectionHtml(nodes),
        sourceMode: "legacy_html",
      };
    case "apps":
      return {
        id: "workspace-collection-apps",
        title: "Apps",
        designSystem: true,
        html: buildAppsCollectionHtml(nodes),
        sourceMode: "legacy_html",
      };
  }
}

function buildConversationCollectionHtml(conversations: ConversationMeta[]): string {
  const cards =
    conversations.length > 0
      ? conversations
          .slice(0, 8)
          .map(
            (conversation) => `<article class="collection-card">
  <div class="collection-card__body">
    <div class="collection-card__title">${escapeHtml(conversation.title)}</div>
    <div class="collection-card__meta">${escapeHtml(conversation.lastMessage || "No preview yet")}</div>
    <div class="collection-card__hint">${conversation.messageCount} message${conversation.messageCount !== 1 ? "s" : ""} · ${escapeHtml(formatTimestamp(conversation.timestamp))}</div>
  </div>
  <button class="collection-action collection-action--primary" data-open-session="${escapeHtml(conversation.id)}">Open chat</button>
</article>`,
          )
          .join("")
      : `<div class="collection-empty">
  <h2>No conversations yet</h2>
  <p>Your recent chats will show up here so you can jump back in without hunting for them.</p>
  <button class="collection-action collection-action--primary" data-new-conversation="true">Start a conversation</button>
</div>`;

  return buildCollectionHtml({
    kicker: "Workspace",
    title: "Conversations",
    description:
      "Pick up a recent chat or start a new one. This should be the same place you expect conversation history to live.",
    summary: `${conversations.length} saved conversation${conversations.length !== 1 ? "s" : ""}`,
    content: cards,
  });
}

function buildProjectsCollectionHtml(nodes: WorkspaceNode[]): string {
  const projects = getRootChildren(nodes, "projects").filter((node) => node.type === "project");
  const cards =
    projects.length > 0
      ? projects
          .map(
            (project) => `<article class="collection-card">
  <div class="collection-card__body">
    <div class="collection-card__title">${escapeHtml(project.label)}</div>
    <div class="collection-card__meta">${escapeHtml(String(project.metadata?.status ?? "Active"))}</div>
    <div class="collection-card__hint">${escapeHtml(String(project.metadata?.owner ?? "Managed with your agent"))}</div>
  </div>
  <button class="collection-action" data-open-node="${escapeHtml(project.id)}">Open project</button>
</article>`,
          )
          .join("")
      : `<div class="collection-empty">
  <h2>No projects yet</h2>
  <p>Projects are the bigger things you and your agent are planning together. Ask your agent to turn a goal into a project and it will show up here.</p>
  <button class="collection-action collection-action--primary" data-new-conversation="true">Create a project</button>
</div>`;

  return buildCollectionHtml({
    kicker: "Workspace",
    title: "Projects",
    description: "Projects hold the bigger outcomes you are planning and tracking with your agent.",
    summary: `${projects.length} project${projects.length !== 1 ? "s" : ""}`,
    content: cards,
  });
}

function buildWorkstreamsCollectionHtml(nodes: WorkspaceNode[]): string {
  const workstreams = getRootChildren(nodes, "workstreams").filter(
    (node) => node.type === "workstream",
  );
  const cards =
    workstreams.length > 0
      ? workstreams
          .map((workstream) => {
            const openIssueCount =
              workstream.children?.filter((child) => child.type === "issue" && isOpenIssue(child))
                .length ?? 0;
            const progress =
              typeof workstream.metadata?.progress === "number"
                ? `${Math.round(Number(workstream.metadata.progress))}% complete`
                : "No progress yet";
            return `<article class="collection-card">
  <div class="collection-card__body">
    <div class="collection-card__title">${escapeHtml(workstream.label)}</div>
    <div class="collection-card__meta">${escapeHtml(progress)}</div>
    <div class="collection-card__hint">${openIssueCount} open issue${openIssueCount !== 1 ? "s" : ""}</div>
  </div>
  <button class="collection-action" data-open-node="${escapeHtml(workstream.id)}">Open workstream</button>
</article>`;
          })
          .join("")
      : `<div class="collection-empty">
  <h2>No workstreams yet</h2>
  <p>Workstreams are the ongoing loops your agent keeps moving for you, like research, monitoring, or follow-up work.</p>
  <button class="collection-action collection-action--primary" data-new-conversation="true">Start your first workstream</button>
</div>`;

  return buildCollectionHtml({
    kicker: "Workspace",
    title: "Workstreams",
    description:
      "Workstreams are the repeatable or ongoing work your agent keeps advancing in the background.",
    summary: `${workstreams.length} active workstream${workstreams.length !== 1 ? "s" : ""}`,
    content: cards,
  });
}

function buildAppsCollectionHtml(nodes: WorkspaceNode[]): string {
  const apps = getRootChildren(nodes, "apps").filter((node) => node.type === "app");
  const cards =
    apps.length > 0
      ? apps
          .map(
            (app) => `<article class="collection-card">
  <div class="collection-card__body">
    <div class="collection-card__title">${escapeHtml(app.label)}</div>
    <div class="collection-card__meta">Built for a specific way you want to work</div>
    <div class="collection-card__hint">${escapeHtml(String(app.metadata?.status ?? "Ready"))}</div>
  </div>
  <button class="collection-action" data-open-node="${escapeHtml(app.id)}">Open app</button>
</article>`,
          )
          .join("")
      : `<div class="collection-empty">
  <h2>No apps yet</h2>
  <p>When you ask your agent for a custom app or structured tool, it will show up here.</p>
  <button class="collection-action collection-action--primary" data-new-conversation="true">Ask for an app</button>
</div>`;

  return buildCollectionHtml({
    kicker: "Workspace",
    title: "Apps",
    description:
      "Apps are focused tools your agent builds when you want a more structured way to work.",
    summary: `${apps.length} app${apps.length !== 1 ? "s" : ""}`,
    content: cards,
  });
}

function buildCollectionHtml({
  kicker,
  title,
  description,
  summary,
  content,
}: {
  kicker: string;
  title: string;
  description: string;
  summary: string;
  content: string;
}): string {
  return `<style>
  .collection-shell { padding: 18px; display: grid; gap: 16px; }
  .collection-header { display: grid; gap: 6px; }
  .collection-kicker { font-size: 11px; color: var(--c-text-faint); text-transform: uppercase; letter-spacing: 0.08em; }
  .collection-title { font-size: 24px; font-weight: 700; color: var(--c-text); line-height: 1.1; }
  .collection-description { font-size: 14px; color: var(--c-text-secondary); max-width: 640px; }
  .collection-summary { display: inline-flex; align-items: center; gap: 8px; width: fit-content; padding: 8px 12px; border-radius: 999px; background: var(--c-accent-subtle); color: var(--c-text); font-size: 12px; font-weight: 600; }
  .collection-summary__dot { width: 8px; height: 8px; border-radius: 999px; background: var(--c-accent); }
  .collection-grid { display: grid; gap: 12px; }
  .collection-card { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 14px; border: 1px solid var(--c-border); border-radius: 14px; background: var(--c-surface); }
  .collection-card__body { display: grid; gap: 4px; min-width: 0; }
  .collection-card__title { font-size: 15px; font-weight: 600; color: var(--c-text); }
  .collection-card__meta { font-size: 13px; color: var(--c-text-secondary); }
  .collection-card__hint { font-size: 12px; color: var(--c-text-faint); }
  .collection-action { border: 1px solid var(--c-border); background: transparent; color: var(--c-text); border-radius: 10px; padding: 8px 12px; font-size: 12px; font-weight: 600; cursor: pointer; white-space: nowrap; }
  .collection-action--primary { background: var(--c-accent); border-color: transparent; color: var(--c-accent-text); }
  .collection-empty { display: grid; gap: 8px; padding: 20px; border: 1px dashed var(--c-border-hover); border-radius: 16px; background: var(--c-surface-2); }
  .collection-empty h2 { font-size: 18px; color: var(--c-text); }
  .collection-empty p { font-size: 13px; color: var(--c-text-secondary); max-width: 560px; }
  @media (max-width: 720px) {
    .collection-shell { padding: 14px; gap: 12px; }
    .collection-card { flex-direction: column; align-items: flex-start; }
    .collection-action { width: 100%; }
  }
</style>
<div class="collection-shell">
  <header class="collection-header">
    <div class="collection-kicker">${escapeHtml(kicker)}</div>
    <div class="collection-title">${escapeHtml(title)}</div>
    <div class="collection-description">${escapeHtml(description)}</div>
    <div class="collection-summary"><span class="collection-summary__dot"></span>${escapeHtml(summary)}</div>
  </header>
  <section class="collection-grid">${content}</section>
</div>
<script>
  document.querySelectorAll('[data-open-session]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const sessionId = btn.getAttribute('data-open-session');
      if (sessionId && window.agentAction) {
        window.agentAction('navigate', { target: 'conversation', sessionId });
      }
    });
  });
  document.querySelectorAll('[data-open-node]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const nodeId = btn.getAttribute('data-open-node');
      if (nodeId && window.agentAction) {
        window.agentAction('navigate', { target: 'node', nodeId });
      }
    });
  });
  document.querySelectorAll('[data-new-conversation]').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (window.agentAction) {
        window.agentAction('custom', { kind: 'new_conversation' });
      }
    });
  });
</script>`;
}

function getRootChildren(nodes: WorkspaceNode[], rootId: WorkspaceCollectionId): WorkspaceNode[] {
  return nodes.find((node) => node.id === rootId)?.children ?? [];
}

function isOpenIssue(node: WorkspaceNode): boolean {
  const status = String(node.metadata?.status ?? "open").toLowerCase();
  return !["resolved", "closed", "done", "archived"].includes(status);
}

function formatTimestamp(timestamp: number): string {
  return new Date(timestamp).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}
